/**
 * JWE Encryption Service for Frontend
 * Uses ECDH-ES + A256GCM algorithm
 */
import * as jose from 'jose';

// Cache for server's public key
let serverPublicKey = null;
let encryptionEnabled = false;

/**
 * Fetch and cache server's public key
 */
export const fetchServerPublicKey = async () => {
  try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const response = await fetch(`${backendUrl}/api/encryption/public-key`);
    const data = await response.json();
    
    encryptionEnabled = data.enabled;
    
    if (data.enabled && data.public_key) {
      // Import JWK to KeyLike object
      serverPublicKey = await jose.importJWK(data.public_key, 'ECDH-ES');
    }
    
    return { enabled: encryptionEnabled, publicKey: serverPublicKey };
  } catch (error) {
    console.error('Failed to fetch server public key:', error);
    encryptionEnabled = false;
    return { enabled: false, publicKey: null };
  }
};

/**
 * Check if encryption is enabled
 */
export const isEncryptionEnabled = () => encryptionEnabled;

/**
 * Encrypt data using JWE with ECDH-ES + A256GCM
 * @param {object} data - Data to encrypt
 * @returns {string} JWE token or JSON string if encryption disabled
 */
export const encryptData = async (data) => {
  if (!encryptionEnabled || !serverPublicKey) {
    return JSON.stringify(data);
  }
  
  try {
    const plaintext = new TextEncoder().encode(JSON.stringify(data));
    
    const jwe = await new jose.CompactEncrypt(plaintext)
      .setProtectedHeader({ 
        alg: 'ECDH-ES', 
        enc: 'A256GCM',
        typ: 'JWE'
      })
      .encrypt(serverPublicKey);
    
    return jwe;
  } catch (error) {
    console.error('Encryption failed:', error);
    return JSON.stringify(data);
  }
};

/**
 * Decrypt JWE token
 * Note: For ECDH-ES, the client needs its own key pair to decrypt responses
 * This is typically used when server encrypts response to client
 */
export const decryptData = async (jweToken, privateKey) => {
  if (!encryptionEnabled || !privateKey) {
    try {
      return JSON.parse(jweToken);
    } catch {
      return jweToken;
    }
  }
  
  try {
    const { plaintext } = await jose.compactDecrypt(jweToken, privateKey);
    return JSON.parse(new TextDecoder().decode(plaintext));
  } catch (error) {
    console.error('Decryption failed:', error);
    // Try to parse as plain JSON
    try {
      return JSON.parse(jweToken);
    } catch {
      return jweToken;
    }
  }
};

/**
 * Generate client key pair for receiving encrypted responses
 */
export const generateClientKeyPair = async () => {
  try {
    const { publicKey, privateKey } = await jose.generateKeyPair('ECDH-ES', {
      crv: 'P-256',
      extractable: true
    });
    
    // Export public key as JWK to send to server
    const publicJwk = await jose.exportJWK(publicKey);
    publicJwk.use = 'enc';
    publicJwk.alg = 'ECDH-ES+A256GCM';
    
    return {
      publicKey,
      privateKey,
      publicJwk
    };
  } catch (error) {
    console.error('Failed to generate key pair:', error);
    return null;
  }
};

/**
 * Initialize encryption - call this on app startup
 */
export const initializeEncryption = async () => {
  const result = await fetchServerPublicKey();
  console.log('Encryption initialized:', result.enabled ? 'ENABLED' : 'DISABLED');
  return result;
};

export default {
  fetchServerPublicKey,
  isEncryptionEnabled,
  encryptData,
  decryptData,
  generateClientKeyPair,
  initializeEncryption
};
